import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, Trash2, Edit2, Users } from 'lucide-react';
import api from '@/lib/api';
import EmptyState from '@/components/common/EmptyState';
import { toast } from 'sonner';

export default function CustomersManager() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get('/customers');
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => items.filter((c) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (c.name || '').toLowerCase().includes(q)
      || (c.company || '').toLowerCase().includes(q)
      || (c.whatsapp || '').toLowerCase().includes(q)
      || (c.location || '').toLowerCase().includes(q);
  }), [items, query]);

  const remove = async (c) => {
    try {
      const { data } = await api.delete(`/customers/${c.id}`);
      const orphan = data?.orphaned_transactions || 0;
      if (orphan > 0) {
        toast.success(`Customer "${c.name}" dihapus. ${orphan} riwayat transaksi tetap tersimpan (snapshot).`);
      } else {
        toast.success(`Customer "${c.name}" dihapus`);
      }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal hapus'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Cari nama, perusahaan, WA, lokasi..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" data-testid="customers-search" />
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-customer-button"><Plus className="h-4 w-4 mr-2" />Tambah Customer</Button>
          </DialogTrigger>
          <CustomerFormDialog editing={editing} onSaved={() => { load(); setOpen(false); setEditing(null); }} />
        </Dialog>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Users} title="Belum ada customer" description="Tambahkan customer baru untuk mulai transaksi." />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nama</TableHead>
                    <TableHead>Perusahaan</TableHead>
                    <TableHead>WhatsApp</TableHead>
                    <TableHead>NPWP</TableHead>
                    <TableHead>Lokasi</TableHead>
                    <TableHead className="text-right">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((c) => (
                    <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell>{c.company || <span className="text-muted-foreground">-</span>}</TableCell>
                      <TableCell className="font-mono text-xs">{c.whatsapp || <span className="text-muted-foreground">-</span>}</TableCell>
                      <TableCell className="font-mono text-xs">{c.npwp || <span className="text-muted-foreground">-</span>}</TableCell>
                      <TableCell>{c.location || <span className="text-muted-foreground">-</span>}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button size="sm" variant="outline" onClick={() => { setEditing(c); setOpen(true); }} data-testid={`edit-customer-${c.id}`}>
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200" data-testid={`delete-customer-${c.id}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent data-testid={`delete-customer-confirm-${c.id}`}>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Hapus customer "{c.name}"?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Customer akan dihapus dari daftar. Riwayat transaksi tetap tersimpan karena nama, perusahaan & WA sudah disimpan sebagai snapshot per transaksi.
                                {c.company && <><br /><b>Perusahaan:</b> {c.company}</>}
                                {c.whatsapp && <><br /><b>WhatsApp:</b> {c.whatsapp}</>}
                                {c.location && <><br /><b>Lokasi:</b> {c.location}</>}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Batal</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => remove(c)}
                                className="bg-rose-600 hover:bg-rose-700"
                                data-testid={`delete-customer-confirm-btn-${c.id}`}
                              >
                                Ya, Hapus
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function CustomerFormDialog({ editing, onSaved, inline = false }) {
  const [name, setName] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [npwp, setNpwp] = useState('');
  const [location, setLocation] = useState('');
  const [company, setCompany] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing) {
      setName(editing.name || '');
      setWhatsapp(editing.whatsapp || '');
      setNpwp(editing.npwp || '');
      setLocation(editing.location || '');
      setCompany(editing.company || '');
    } else {
      setName(''); setWhatsapp(''); setNpwp(''); setLocation(''); setCompany('');
    }
  }, [editing]);

  const submit = async () => {
    if (!name.trim()) return toast.error('Nama customer wajib diisi');
    setSaving(true);
    try {
      const body = { name: name.trim(), whatsapp: whatsapp || null, npwp: npwp || null, location: location || null, company: company || null };
      let saved;
      if (editing) {
        saved = (await api.patch(`/customers/${editing.id}`, body)).data;
        toast.success('Customer diperbarui');
      } else {
        saved = (await api.post('/customers', body)).data;
        toast.success('Customer ditambahkan');
      }
      onSaved?.(saved);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal simpan'); }
    finally { setSaving(false); }
  };

  const Fields = (
    <div className="space-y-3">
      <div className="rounded-lg border-2 border-primary/20 bg-accent/40 p-3" data-testid="customer-naming-guide">
        <div className="text-xs font-semibold text-accent-foreground mb-1 flex items-center gap-1.5">
          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold">i</span>
          Format Penulisan Nama Customer
        </div>
        <div className="text-xs text-accent-foreground/90">
          Gunakan format standar berikut agar seragam:
        </div>
        <div className="mt-1.5 rounded-md bg-white border px-2 py-1.5 font-mono text-[11px] text-foreground">
          Nama Kostumer - Perusahaan/Pemdes/Kelompok - Kabupaten - Nama Desa
        </div>
        <div className="mt-1 text-[10px] text-muted-foreground italic">
          Contoh: <span className="font-medium not-italic">Pak Budi - Pemdes Sukamaju - Kab. Bogor - Ds. Sukamaju</span>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Nama Customer <span className="text-rose-600">*</span></Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Pak Budi - Pemdes Sukamaju - Kab. Bogor - Ds. Sukamaju" data-testid="customer-name" autoFocus />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Nomor WhatsApp</Label>
          <Input value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="08xxxxxxxx" data-testid="customer-whatsapp" />
        </div>
        <div className="space-y-1.5">
          <Label>NPWP (opsional)</Label>
          <Input value={npwp} onChange={(e) => setNpwp(e.target.value)} placeholder="00.000.000.0-000.000" data-testid="customer-npwp" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Perusahaan / Pemdes / Kelompok Customer</Label>
        <Input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="mis. PT. Sumber Makmur / Pemdes Sukamaju / Kelompok Tani Maju" data-testid="customer-company" />
      </div>
      <div className="space-y-1.5">
        <Label>Lokasi Customer</Label>
        <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Kabupaten - Nama Desa" data-testid="customer-location" />
      </div>
    </div>
  );

  if (inline) {
    return (
      <div className="space-y-4">
        {Fields}
        <Button onClick={submit} className="w-full" disabled={saving} data-testid="customer-save-button">
          {saving ? 'Menyimpan...' : (editing ? 'Simpan Perubahan' : 'Simpan Customer')}
        </Button>
      </div>
    );
  }

  return (
    <DialogContent className="sm:max-w-md" data-testid="customer-dialog">
      <DialogHeader>
        <DialogTitle>{editing ? 'Edit Customer' : 'Tambah Customer'}</DialogTitle>
      </DialogHeader>
      {Fields}
      <DialogFooter>
        <Button onClick={submit} disabled={saving} data-testid="customer-save-button">
          {saving ? 'Menyimpan...' : (editing ? 'Simpan Perubahan' : 'Simpan Customer')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
